/**
 * Ajuda em vídeo da tela atual.
 *
 * O ícone só aparece quando existe vídeo cadastrado para o caminho (Admin >
 * Vídeos de ajuda). Ao clicar, o vídeo abre centralizado com o resto da tela
 * escurecido. Usa a IFrame API do YouTube (youtube.com) sem vídeos sugeridos.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import api from "../utils/api";

let cache = null;
let carregando = null;

async function carregarVideos() {
  if (cache) return cache;
  if (!carregando) {
    carregando = api
      .get("/help-videos")
      .then(({ data }) => {
        cache = data.items || [];
        return cache;
      })
      .catch(() => {
        cache = [];
        return cache;
      });
  }
  return carregando;
}

/** Limpa o cache quando a lista muda no /admin. */
export function limparCacheAjuda() {
  cache = null;
  carregando = null;
}

/** Casa o caminho exato e, não achando, o prefixo mais específico. */
function videoDoCaminho(itens, pathname) {
  const exato = itens.find((i) => i.path === pathname);
  if (exato) return exato;
  return (
    itens
      .filter((i) => i.path !== "/" && pathname.startsWith(`${i.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0] || null
  );
}

/** Cursor/play animado — ícone de ajuda da plataforma. */
function IconeAjuda({ size = 22 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="ajuda-video-ic"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <g className="scr">
        <rect x="2" y="3" width="16" height="12" rx="2" />
        <path className="of" d="M8.4 6.5v5l4.2-2.5z" />
      </g>
      <circle className="rip o" cx="10.8" cy="10.8" r="3" />
      <g className="cur">
        <path
          transform="translate(8 8) scale(.7)"
          d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z"
          style={{ fill: "#fff" }}
        />
      </g>
    </svg>
  );
}

let ytApi = null;

/** Carrega a IFrame API do YouTube uma única vez. */
function carregarYtApi() {
  if (ytApi) return ytApi;
  ytApi = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const anterior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      anterior && anterior();
      resolve(window.YT);
    };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(s);
    return undefined;
  });
  return ytApi;
}

/**
 * Player do YouTube sem autoplay. O YouTube guarda "mudo" no localStorage do
 * navegador; por isso, quando o usuário dá play (gesto válido), forçamos
 * unMute + volume 100 via API.
 */
function Player({ videoId, titulo }) {
  const alvo = useRef(null);

  useEffect(() => {
    let player = null;
    let cancelado = false;
    const ligarSom = (p) => {
      try {
        p.unMute();
        p.setVolume(100);
      } catch {
        /* player já destruído */
      }
    };
    carregarYtApi().then((YT) => {
      if (cancelado || !alvo.current) return;
      player = new YT.Player(alvo.current, {
        host: "https://www.youtube.com",
        videoId,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1, mute: 0 },
        events: {
          onReady: (e) => ligarSom(e.target),
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING && e.target.isMuted()) ligarSom(e.target);
          },
        },
      });
    });
    return () => {
      cancelado = true;
      try {
        player && player.destroy();
      } catch {
        /* ignorar */
      }
    };
  }, [videoId]);

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl bg-black shadow-2xl ring-1 ring-white/10 aspect-video"
      data-testid="help-video-player"
    >
      <div ref={alvo} title={titulo} className="absolute inset-0 h-full w-full [&>iframe]:h-full [&>iframe]:w-full" />
    </div>
  );
}

export default function AjudaEmVideo() {
  const location = useLocation();
  const [itens, setItens] = useState(null);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    carregarVideos().then(setItens);
  }, []);

  useEffect(() => {
    setAberto(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!aberto) return undefined;
    const esc = (e) => e.key === "Escape" && setAberto(false);
    window.addEventListener("keydown", esc);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = overflowAnterior;
    };
  }, [aberto]);

  const video = itens ? videoDoCaminho(itens, location.pathname) : null;
  if (!video) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="ajuda-video-btn shrink-0 ml-1 rounded-md p-1 transition-colors hover:bg-gray-100"
        title={video.title || "Ver vídeo desta tela"}
        data-testid="help-video-btn"
      >
        <IconeAjuda />
      </button>

      {aberto && createPortal(
        <div
          className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150"
          onClick={() => setAberto(false)}
          data-testid="help-video-overlay"
        >
          <div
            className="relative w-full"
            style={{ maxWidth: "min(820px, 92vw, 124vh)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-sm font-semibold text-white/90 truncate">
                {video.title || "Como usar esta tela"}
              </p>
              <div className="flex items-center gap-1">
                <a
                  href={`https://youtu.be/${video.youtube_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full px-2.5 py-1 text-xs font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                  data-testid="help-video-open-youtube"
                >
                  Abrir no YouTube
                </a>
                <button
                  type="button"
                  onClick={() => setAberto(false)}
                  className="rounded-full p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                  title="Fechar (Esc)"
                  data-testid="help-video-close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <Player videoId={video.youtube_id} titulo={video.title || "Vídeo de ajuda"} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
